/**
 * /accounting/ap — Bills hub. List, age, status, quick-pay, new bill.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, ChevronRight, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useAP } from "@/data/accounting/ap/APContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { NewBillDialog } from "@/components/accounting/NewBillDialog";
import { RecordPaymentDialog } from "@/components/accounting/RecordPaymentDialog";
import type { Bill } from "@/data/accounting/ap/apTypes";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function ageDays(dueDate: string) {
  return Math.floor((Date.now() - new Date(dueDate).getTime()) / (24 * 60 * 60 * 1000));
}

export default function APBillsPage() {
  const { bills } = useAP();
  const { getVendorById } = useVendorPO();
  const { accountById } = useGeneralLedger();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "open" | "overdue" | "paid">("all");
  const [showNew, setShowNew] = useState(false);
  const [paying, setPaying] = useState<Bill | null>(null);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return bills
      .filter(b => {
        if (filter === "open") return b.status === "open" || b.status === "partially_paid";
        if (filter === "overdue") return (b.status === "open" || b.status === "partially_paid") && ageDays(b.dueDate) > 0;
        if (filter === "paid") return b.status === "paid";
        return true;
      })
      .filter(b => !s ||
        b.vendorName.toLowerCase().includes(s) ||
        b.invoiceNumber.toLowerCase().includes(s));
  }, [bills, filter, search]);

  const totals = useMemo(() => {
    const open = bills.filter(b => b.status === "open" || b.status === "partially_paid");
    const overdue = open.filter(b => ageDays(b.dueDate) > 0);
    return {
      openBalance: open.reduce((s, b) => s + (b.total - b.amountPaid), 0),
      openCount: open.length,
      overdueBalance: overdue.reduce((s, b) => s + (b.total - b.amountPaid), 0),
      overdueCount: overdue.length,
    };
  }, [bills]);

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 data-tour-id="ap-bills-table" className="text-2xl font-semibold tracking-tight">Accounts Payable</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vendor bills. Each bill posts DR cost / CR AP automatically. Payments clear AP and hit cash.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/accounting/ap/vendor-statements">
              <FileText className="h-4 w-4 mr-1" /> Vendor Statements
            </Link>
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Bill
          </Button>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">Open balance</div>
            <div className="text-lg font-semibold tabular-nums">{fmt(totals.openBalance)}</div>
            <div className="text-[11px] text-muted-foreground">{totals.openCount} bills</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">Overdue</div>
            <div className={`text-lg font-semibold tabular-nums ${totals.overdueCount > 0 ? "text-destructive" : ""}`}>
              {fmt(totals.overdueBalance)}
            </div>
            <div className="text-[11px] text-muted-foreground">{totals.overdueCount} bills</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">Posted bills</div>
            <div className="text-lg font-semibold tabular-nums">{bills.length}</div>
            <div className="text-[11px] text-muted-foreground">All-time</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">AP account</div>
            <div className="text-sm font-medium">2000 · Accounts Payable</div>
            <div className="text-[11px] text-muted-foreground">By-vendor subledger</div>
          </div>
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <div className="flex items-center gap-1">
              {(["all", "open", "overdue", "paid"] as const).map(k => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`text-xs px-2 py-1 rounded-sm border ${
                    filter === k ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-transparent" : "border-border hover:bg-accent/40"
                  }`}
                >
                  {k[0].toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search vendor / invoice…" className="h-7 text-xs w-56" />
          </div>
          <div className="divide-y divide-border max-h-[560px] overflow-auto">
            {filtered.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No bills. Click <span className="font-medium">New Bill</span> to post one.
              </div>
            )}
            {filtered.map(b => {
              const open = b.total - b.amountPaid;
              const overdue = ageDays(b.dueDate);
              const isOverdue = (b.status === "open" || b.status === "partially_paid") && overdue > 0;
              return (
                <div key={b.id} className="p-3 hover:bg-accent/30 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        <span>{b.vendorName} <span className="text-muted-foreground">· {b.invoiceNumber}</span></span>
                        {getVendorById(b.vendorId)?.is1099Eligible && (
                          <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase tracking-wide border-amber-500/40 text-amber-600">
                            1099
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Bill {fmtDate(b.billDate)} · Due {fmtDate(b.dueDate)}
                        {isOverdue && <span className="text-destructive ml-2">{overdue}d overdue</span>}
                        · {b.lines.length} line{b.lines.length === 1 ? "" : "s"}
                        {b.lines.some(l => l.jobId) && " · job-tagged"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold tabular-nums">{fmt(b.total)}</div>
                      <div className="text-[11px] text-muted-foreground tabular-nums">
                        {b.status === "paid"
                          ? "Paid in full"
                          : `${fmt(open)} open`}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0 w-20">
                      <Badge
                        variant={b.status === "paid" ? "secondary" :
                          b.status === "void" ? "outline" :
                          isOverdue ? "destructive" : "outline"}
                        className="h-4 px-1 text-[10px] uppercase tracking-wide"
                      >
                        {b.status.replace("_", " ")}
                      </Badge>
                      {b.status !== "paid" && b.status !== "void" && (
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                          onClick={() => setPaying(b)}>
                          <Banknote className="h-3 w-3 mr-1" /> Pay
                        </Button>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  {b.lines.length > 0 && (
                    <div className="mt-2 pl-2 border-l-2 border-border space-y-0.5">
                      {b.lines.slice(0, 3).map(l => {
                        const acc = accountById.get(l.glAccountId);
                        return (
                          <div key={l.id} className="text-[11px] text-muted-foreground flex justify-between gap-2">
                            <span className="truncate">
                              {acc?.number} {acc?.name}
                              {l.jobId && ` · ${l.jobId}`}
                              {l.tradeType && ` · ${l.tradeType}`}
                              {l.description && ` · ${l.description}`}
                            </span>
                            <span className="tabular-nums">{fmt(l.amount)}</span>
                          </div>
                        );
                      })}
                      {b.lines.length > 3 && (
                        <div className="text-[11px] text-muted-foreground">
                          +{b.lines.length - 3} more lines
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CockpitSection>

      <NewBillDialog open={showNew} onOpenChange={setShowNew} />
      <RecordPaymentDialog bill={paying} onOpenChange={(o) => !o && setPaying(null)} />
    </CockpitPage>
  );
}
